/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { ChangeEvent, useMemo, useState } from 'react';

import classNames from 'classnames';

import type * as Type from '@/common/interface';
import Icon from '@/components/Icon';
import { createVideoUploadSession, useVideoUploadConfig } from '@/services';

import './index.scss';

export type MediaAttachmentStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface PendingMediaAttachment {
  localId: string;
  file: File;
  mediaType: Type.VideoMediaType;
  title: string;
  summary: string;
  priceAmount: number;
}

export interface MediaAttachmentUploadTarget {
  questionId: string;
  answerId?: string;
  sourcePage: string;
  onStatusChange?: (
    localId: string,
    status: MediaAttachmentStatus,
    error?: string,
  ) => void;
}

interface Props {
  value: PendingMediaAttachment[];
  onChange: (value: PendingMediaAttachment[]) => void;
  disabled?: boolean;
  itemStatus?: Record<string, MediaAttachmentStatus>;
  title?: string;
  description?: string;
}

const VIDEO_UPLOAD_EXTENSIONS = [
  'mp4',
  'webm',
  'mov',
  'm4v',
  'mkv',
  'avi',
  'm3u8',
];

const DEFAULT_FILE_UPLOAD_EXTENSIONS = [
  'zip',
  '7z',
  'rar',
  'tar',
  'gz',
  'tgz',
  'xz',
  'bz2',
  'dmg',
  'pkg',
  'exe',
  'msi',
  'apk',
  'jar',
];

export const formatAttachmentSize = (size: number) => {
  if (!size) {
    return '';
  }
  if (size >= 1024 * 1024 * 1024) {
    return `${Math.round((size / 1024 / 1024 / 1024) * 10) / 10} GB`;
  }
  if (size >= 1024 * 1024) {
    return `${Math.round((size / 1024 / 1024) * 10) / 10} MB`;
  }
  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

const getUploadExtension = (filename: string) =>
  filename.includes('.') ? filename.split('.').pop()?.toLowerCase() || '' : '';

const getFileExtensions = (uploadConfig?: Type.VideoUploadConfigResp) => {
  if (uploadConfig?.allowed_file_extensions?.length) {
    return uploadConfig.allowed_file_extensions.map((item) =>
      item.replace(/^\./, '').toLowerCase(),
    );
  }
  return DEFAULT_FILE_UPLOAD_EXTENSIONS;
};

const isAllowedUploadFile = (
  file: File,
  mediaType: Type.VideoMediaType,
  uploadConfig?: Type.VideoUploadConfigResp,
) => {
  const ext = getUploadExtension(file.name);
  if (mediaType === 'video') {
    return (
      file.type.startsWith('video/') || VIDEO_UPLOAD_EXTENSIONS.includes(ext)
    );
  }
  return getFileExtensions(uploadConfig).includes(ext);
};

const getUploadAccept = (
  mediaType: Type.VideoMediaType,
  uploadConfig?: Type.VideoUploadConfigResp,
) => {
  if (mediaType === 'video') {
    return 'video/*,.mp4,.webm,.mov,.m4v,.mkv,.avi,.m3u8';
  }
  return getFileExtensions(uploadConfig)
    .map((item) => `.${item}`)
    .join(',');
};

const createLocalId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const normalizePriceAmount = (value: number | string) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return 1;
  }
  return Math.min(1000, Math.max(1, Math.floor(amount)));
};

const getStatusText = (status?: MediaAttachmentStatus) => {
  switch (status) {
    case 'uploading':
      return '上传中';
    case 'done':
      return '已上传';
    case 'error':
      return '失败';
    default:
      return '待提交';
  }
};

export const uploadPendingMediaAttachments = async (
  attachments: PendingMediaAttachment[],
  target: MediaAttachmentUploadTarget,
) => {
  await attachments.reduce<Promise<void>>(async (chain, attachment) => {
    await chain;
    target.onStatusChange?.(attachment.localId, 'uploading');
    try {
      const contentType = attachment.file.type || 'application/octet-stream';
      const session = await createVideoUploadSession({
        question_id: target.questionId,
        answer_id: target.answerId,
        title: attachment.title.trim() || attachment.file.name,
        summary: attachment.summary.trim(),
        filename: attachment.file.name,
        content_type: contentType,
        file_size: attachment.file.size,
        media_type: attachment.mediaType,
        visibility: 'paid_play',
        price_amount: normalizePriceAmount(attachment.priceAmount),
        upload_method: 'put',
        source_page: target.sourcePage,
      });
      const headers = new Headers(session.required_headers || {});
      headers.set('Content-Type', contentType);
      const uploadResp = await fetch(session.upload_url, {
        method: 'PUT',
        headers,
        body: attachment.file,
      });
      if (!uploadResp.ok) {
        throw new Error(`upload failed: ${uploadResp.status}`);
      }
      target.onStatusChange?.(attachment.localId, 'done');
    } catch (err) {
      target.onStatusChange?.(
        attachment.localId,
        'error',
        err instanceof Error ? err.message : 'upload failed',
      );
      throw err;
    }
  }, Promise.resolve());
};

const MediaAttachmentInput = ({
  value,
  onChange,
  disabled = false,
  itemStatus = {},
  title = '附加视频 / 文件',
  description = '随内容一起提交，视频最大 900M，打包文件最大 100M。',
}: Props) => {
  const [mediaType, setMediaType] = useState<Type.VideoMediaType>('video');
  const [error, setError] = useState('');
  const { data: uploadConfig } = useVideoUploadConfig();

  const maxBytes = useMemo(() => {
    if (!uploadConfig) {
      return 0;
    }
    return mediaType === 'video'
      ? uploadConfig.max_video_upload_bytes
      : uploadConfig.max_file_upload_bytes;
  }, [mediaType, uploadConfig]);

  if (!uploadConfig?.enabled) {
    return null;
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    setError('');
    if (files.length === 0) {
      return;
    }

    const result = files.reduce<{
      attachments: PendingMediaAttachment[];
      error: string;
    }>(
      (acc, file) => {
        if (maxBytes && file.size > maxBytes) {
          return {
            ...acc,
            error:
              mediaType === 'video'
                ? '视频不能超过 900M'
                : '下载文件不能超过 100M',
          };
        }
        if (!isAllowedUploadFile(file, mediaType, uploadConfig)) {
          return {
            ...acc,
            error:
              mediaType === 'video'
                ? '请上传常见视频格式'
                : '请上传 zip、7z、rar 等打包文件',
          };
        }
        return {
          ...acc,
          attachments: [
            ...acc.attachments,
            {
              localId: createLocalId(),
              file,
              mediaType,
              title: file.name.replace(/\.[^.]+$/, ''),
              summary: '',
              priceAmount: 1,
            },
          ],
        };
      },
      { attachments: [], error: '' },
    );

    if (result.error) {
      setError(result.error);
    }
    if (result.attachments.length > 0) {
      onChange([...value, ...result.attachments]);
    }
  };

  const updateAttachment = (
    localId: string,
    patch: Partial<
      Pick<PendingMediaAttachment, 'title' | 'summary' | 'priceAmount'>
    >,
  ) => {
    onChange(
      value.map((item) =>
        item.localId === localId ? { ...item, ...patch } : item,
      ),
    );
  };

  const removeAttachment = (localId: string) => {
    onChange(value.filter((item) => item.localId !== localId));
  };

  return (
    <div className="media-attachment-input">
      <div className="media-attachment-input-head">
        <div>
          <h6>{title}</h6>
          <p>{description}</p>
        </div>
        <Icon name="paperclip" />
      </div>
      <div className="media-attachment-tabs" role="tablist">
        <button
          type="button"
          className={classNames(mediaType === 'video' && 'active')}
          disabled={disabled}
          onClick={() => {
            setMediaType('video');
            setError('');
          }}>
          视频
        </button>
        <button
          type="button"
          className={classNames(mediaType === 'file' && 'active')}
          disabled={disabled}
          onClick={() => {
            setMediaType('file');
            setError('');
          }}>
          文件
        </button>
      </div>
      <label className="media-attachment-picker">
        <input
          type="file"
          accept={getUploadAccept(mediaType, uploadConfig)}
          multiple
          disabled={disabled}
          onChange={handleFileChange}
        />
        <Icon name="cloud-upload" />
        <span>
          {mediaType === 'video'
            ? '选择视频，随发布一起上传'
            : '选择打包文件，随发布一起上传'}
        </span>
      </label>
      {error && <div className="media-attachment-error">{error}</div>}
      {value.length > 0 && (
        <div className="media-attachment-list">
          {value.map((item) => {
            const status = itemStatus[item.localId] || 'queued';
            return (
              <div key={item.localId} className="media-attachment-item">
                <div className="media-attachment-item-main">
                  <span className="media-attachment-file-icon">
                    <Icon
                      name={
                        item.mediaType === 'video'
                          ? 'play-btn'
                          : 'file-earmark-zip'
                      }
                    />
                  </span>
                  <div>
                    <strong>{item.file.name}</strong>
                    <small>
                      {item.mediaType === 'video' ? '视频' : '文件'} ·{' '}
                      {formatAttachmentSize(item.file.size)} ·{' '}
                      {normalizePriceAmount(item.priceAmount)} 创作币解锁 ·{' '}
                      {getStatusText(status)}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="media-attachment-remove"
                    disabled={disabled || status === 'uploading'}
                    onClick={() => removeAttachment(item.localId)}
                    aria-label="移除附件">
                    <Icon name="x-lg" />
                  </button>
                </div>
                <input
                  className="form-control form-control-sm"
                  value={item.title}
                  disabled={disabled}
                  placeholder="标题"
                  onChange={(event) =>
                    updateAttachment(item.localId, {
                      title: event.currentTarget.value,
                    })
                  }
                />
                <div className="media-attachment-price-row">
                  <label htmlFor={`media-price-${item.localId}`}>
                    解锁价格
                  </label>
                  <div className="media-attachment-price-control">
                    <input
                      id={`media-price-${item.localId}`}
                      className="form-control form-control-sm"
                      type="number"
                      min={1}
                      max={1000}
                      step={1}
                      value={item.priceAmount}
                      disabled={disabled}
                      onBlur={(event) =>
                        updateAttachment(item.localId, {
                          priceAmount: normalizePriceAmount(
                            event.currentTarget.value,
                          ),
                        })
                      }
                      onChange={(event) =>
                        updateAttachment(item.localId, {
                          priceAmount:
                            event.currentTarget.value === ''
                              ? 1
                              : normalizePriceAmount(event.currentTarget.value),
                        })
                      }
                    />
                    <span>创作币</span>
                  </div>
                  <small>用户支付一次后永久解锁，收益进入发布者钱包。</small>
                </div>
                <textarea
                  className="form-control form-control-sm"
                  value={item.summary}
                  rows={2}
                  disabled={disabled}
                  placeholder="补充说明，可写安装环境、演示重点或文件用途"
                  onChange={(event) =>
                    updateAttachment(item.localId, {
                      summary: event.currentTarget.value,
                    })
                  }
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MediaAttachmentInput;
